import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { dienstleisterAPI } from '@/lib/api';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import {
  trackDlPageOpen, trackDlPricingView, trackDlMapClick,
  trackDlFormStart, trackDlFormSubmit, trackDlFormSuccess,
} from '@/lib/tracking';
import { useWizardDraft } from '@/hooks/useWizardDraft';
import { StepBasisdaten } from '@/components/dienstleister/StepBasisdaten';
import { StepLeistungen } from '@/components/dienstleister/StepLeistungen';
import { StepRegionen } from '@/components/dienstleister/StepRegionen';
import { StepZertifikate } from '@/components/dienstleister/StepZertifikate';
import { StepZusammenfassung } from '@/components/dienstleister/StepZusammenfassung';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { GermanyMap } from '@/components/GermanyMap';
import {
  TreeDeciduous, ArrowRight, Check, CheckCircle2, Shield,
  MapPin, Award, Briefcase, Users, Eye, Loader2,
  Star, Save, RotateCcw, ChevronLeft, Trash2,
} from 'lucide-react';

const getPreisstufen = (t) => [
  {
    name: t('dlPage.stufeBasis'),
    preis: t('dlPage.stufeBasisPreis'),
    sub: t('dlPage.stufeBasisSub'),
    features: [t('dlPage.basisF1'), t('dlPage.basisF2'), t('dlPage.basisF3')],
    hervorgehoben: false,
  },
  {
    name: t('dlPage.stufeSichtbar'),
    preis: t('dlPage.stufeSichtbarPreis'),
    sub: t('dlPage.stufeSichtbarSub'),
    features: [t('dlPage.sichtbarF1'), t('dlPage.sichtbarF2'), t('dlPage.sichtbarF3'), t('dlPage.sichtbarF4')],
    hervorgehoben: true,
  },
  {
    name: t('dlPage.stufeBevorzugt'),
    preis: t('dlPage.stufeBevorzugtPreis'),
    sub: t('dlPage.stufeBevorzugtSub'),
    features: [t('dlPage.bevorzugtF1'), t('dlPage.bevorzugtF2'), t('dlPage.bevorzugtF3'), t('dlPage.bevorzugtF4'), t('dlPage.bevorzugtF5')],
    hervorgehoben: false,
  },
  {
    name: t('dlPage.stufePremium'),
    preis: t('dlPage.stufePremiumPreis'),
    sub: t('dlPage.stufePremiumSub'),
    features: [t('dlPage.premiumF1'), t('dlPage.premiumF2'), t('dlPage.premiumF3'), t('dlPage.premiumF4'), t('dlPage.premiumF5')],
    hervorgehoben: false,
  },
];

const AKTIVITAET_FARBEN = {
  hoch: '#1E5F3A',
  mittel: '#4A9F6E',
  gering: '#A8D5BA',
  minimal: '#e5e5e5',
};

function RegionalKarte({ regionen, selectedBl, onSelect }) {
  const colorMap = {};
  for (const r of regionen) {
    colorMap[r.bundesland] = AKTIVITAET_FARBEN[r.aktivitaet_stufe] || '#e5e5e5';
  }

  const selected = regionen.find(r => r.bundesland === selectedBl);

  return (
    <div className="flex flex-col lg:flex-row gap-6 items-start">
      <div className="w-full lg:w-auto">
        <GermanyMap
          selectedBundesland={selectedBl}
          onSelect={onSelect}
          colorMap={colorMap}
          showHauptstaedte={true}
          height={380}
        />
      </div>

      <div className="flex-1 min-w-0">
        {selected ? (
          <div data-testid="region-detail">
            <h4 className="font-semibold text-foreground mb-1">{selected.bundesland}</h4>
            <Badge variant="outline" className={`text-xs mb-3 ${selected.aktivitaet_stufe === 'hoch' ? 'border-emerald-300 text-emerald-700' : selected.aktivitaet_stufe === 'mittel' ? 'border-amber-300 text-amber-700' : 'border-warm-200 text-muted-foreground'}`}>
              {selected.aktivitaet_label}
            </Badge>
            {selected.top_themen?.length > 0 && (
              <div className="mt-2">
                <p className="text-xs text-muted-foreground mb-1">Häufige Themen:</p>
                <div className="flex flex-wrap gap-1">
                  {selected.top_themen.map((t, i) => (
                    <Badge key={i} variant="outline" className="text-xs">{t}</Badge>
                  ))}
                </div>
              </div>
            )}
            {!selected.hat_echte_daten && (
              <p className="text-xs text-muted-foreground mt-3 italic">
                Basierend auf der allgemeinen Waldstruktur des Bundeslandes. Konkrete Falldaten werden mit wachsender Nutzung ergänzt.
              </p>
            )}
          </div>
        ) : (
          <div className="text-sm text-muted-foreground">
            <p className="mb-2">Klicken Sie auf ein Bundesland, um die regionale Aktivität zu sehen.</p>
            <div className="space-y-1.5 mt-4">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-sm" style={{ background: AKTIVITAET_FARBEN.hoch }} />
                <span className="text-xs">Hohe Nachfrage erwartet</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-sm" style={{ background: AKTIVITAET_FARBEN.mittel }} />
                <span className="text-xs">Grundlegende Nachfrage</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-sm" style={{ background: AKTIVITAET_FARBEN.gering }} />
                <span className="text-xs">Vereinzelte Nachfrage</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-sm" style={{ background: AKTIVITAET_FARBEN.minimal }} />
                <span className="text-xs">Kaum forstliche Aktivität</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const WIZARD_STEP_KEYS = ['stepBasisdaten', 'stepLeistungen', 'stepRegionen', 'stepZertifikate', 'stepAbsenden'];

const INITIAL_FORM = {
  firma: '',
  leistungen: [],
  bundeslaender: [],
  zertifizierungen: [],
  zertifizierungen_freitext: '',
  kontakt_name: '',
  kontakt_email: '',
  kontakt_telefon: '',
  beschreibung: '',
  einsatzradius_km: '',
};

export default function DienstleisterPage() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user } = useAuth();
  const preisstufen = getPreisstufen(t);
  const [optionen, setOptionen] = useState(null);
  const [regionen, setRegionen] = useState([]);
  const [selectedBl, setSelectedBl] = useState(null);
  const [profilErstellt, setProfilErstellt] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [gewaehlteStufe, setGewaehlteStufe] = useState('Basis');
  const [wizardStep, setWizardStep] = useState(1);
  const [form, setForm] = useState({ ...INITIAL_FORM });
  const [sending, setSending] = useState(false);
  const [bestehendesProfil, setBestehendesProfil] = useState(null);

  const { hasDraft, draftLoaded, saveDraft, loadDraft, clearDraft, dismissDraft } = useWizardDraft('dienstleister');

  useEffect(() => {
    trackDlPageOpen();
    dienstleisterAPI.optionen().then(r => setOptionen(r.data)).catch(() => {});
    dienstleisterAPI.regionalUebersicht().then(r => setRegionen(r.data.regionen || [])).catch(() => {});
    // Bestehendes Profil laden (für eingeloggte Nutzer)
    if (user?.email) {
      dienstleisterAPI.meinProfil(user.email).then(r => {
        if (r.data.profil) {
          const p = r.data.profil;
          setProfilErstellt({
            profil_id: p.profil_id,
            firma: p.firma,
            kontakt_name: p.kontakt_name,
            kontakt_email: p.kontakt_email,
            kontakt_telefon: p.kontakt_telefon,
            leistungen: p.leistungen,
            regionen: p.service_regions || p.bundeslaender || [],
            bundeslaender: p.service_regions || [],
            zertifizierungen: p.zertifizierungen || [],
            zertifizierungen_freitext: p.zertifizierungen_freitext || '',
            beschreibung: p.beschreibung || '',
            einsatzradius_km: p.einsatz?.einsatzradius_km || '',
          });
          setBestehendesProfil(p);
        }
      }).catch(() => {});
    }
  }, [user]);

  // Auto-save on form/step changes
  useEffect(() => {
    if (showForm && (wizardStep > 1 || form.firma.length > 2)) {
      saveDraft({ _step: wizardStep, ...form, gewaehlteStufe });
    }
  }, [wizardStep, form, showForm, gewaehlteStufe, saveDraft]);

  // Beforeunload warning
  useEffect(() => {
    const handler = (e) => {
      if (showForm && wizardStep > 1) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [showForm, wizardStep]);

  const resumeDraft = useCallback(() => {
    const draft = loadDraft();
    if (draft) {
      setWizardStep(draft._step || 1);
      setForm({
        firma: draft.firma || '',
        leistungen: draft.leistungen || [],
        bundeslaender: draft.bundeslaender || [],
        zertifizierungen: draft.zertifizierungen || [],
        zertifizierungen_freitext: draft.zertifizierungen_freitext || '',
        kontakt_name: draft.kontakt_name || '',
        kontakt_email: draft.kontakt_email || '',
        kontakt_telefon: draft.kontakt_telefon || '',
        beschreibung: draft.beschreibung || '',
        einsatzradius_km: draft.einsatzradius_km || '',
      });
      if (draft.gewaehlteStufe) setGewaehlteStufe(draft.gewaehlteStufe);
      setShowForm(true);
      toast.success(t('dlWizard.draftWiederhergestellt'));
    }
  }, [loadDraft, t]);

  const canAdvance = (step) => {
    switch (step) {
      case 1: return form.firma.trim() && form.kontakt_name.trim() && form.kontakt_email.trim();
      case 2: return form.leistungen.length > 0;
      case 3: return form.bundeslaender.length > 0;
      case 4: return true;
      default: return true;
    }
  };

  const nextStep = () => {
    if (!canAdvance(wizardStep)) {
      toast.error(t('dlWizard.pflichtfelderFehler'));
      return;
    }
    setWizardStep(s => Math.min(s + 1, 5));
  };

  const prevStep = () => setWizardStep(s => Math.max(s - 1, 1));

  const absenden = async () => {
    setSending(true);
    trackDlFormSubmit();
    try {
      const payload = {
        ...form,
        einsatz: {
          eigenes_land: true,
          grenzueberschreitend: false,
          einsatzlaender: ['DE'],
          einsatzregionen: [],
          einsatzradius_km: form.einsatzradius_km ? parseInt(form.einsatzradius_km) : null,
        },
      };
      const istBearbeitung = !!bestehendesProfil?.profil_id;
      let res;
      if (istBearbeitung) {
        res = await dienstleisterAPI.profilAktualisieren(bestehendesProfil.profil_id, payload);
        toast.success(t('dlWizard.profilAktualisiert'));
      } else {
        res = await dienstleisterAPI.profilErstellen(payload);
        trackDlFormSuccess(res.data.profil_id);
        toast.success(t('dlWizard.profilAngelegtMsg'));
      }
      clearDraft();
      const profilData = {
        ...res.data,
        profil_id: res.data.profil_id || bestehendesProfil?.profil_id,
        firma: form.firma,
        kontakt_name: form.kontakt_name,
        kontakt_email: form.kontakt_email,
        kontakt_telefon: form.kontakt_telefon,
        beschreibung: form.beschreibung,
        leistungen: form.leistungen,
        regionen: form.bundeslaender,
        zertifizierungen: form.zertifizierungen,
        zertifizierungen_freitext: form.zertifizierungen_freitext,
        einsatzradius_km: form.einsatzradius_km,
      };
      setProfilErstellt(profilData);
      setBestehendesProfil(profilData);
      setShowForm(false);
      setWizardStep(1);
    } catch {
      toast.error(t('dlWizard.profilFehler'));
    } finally {
      setSending(false);
    }
  };

  const startWizard = (stufe) => {
    if (stufe) setGewaehlteStufe(stufe);
    trackDlFormStart();
    setShowForm(true);
    setWizardStep(1);
    if (!bestehendesProfil) setForm({ ...INITIAL_FORM });
    setTimeout(() => document.getElementById('profil-section')?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  const startEdit = () => {
    if (profilErstellt) {
      setForm({
        firma: profilErstellt.firma || '',
        leistungen: profilErstellt.leistungen || [],
        bundeslaender: profilErstellt.regionen || profilErstellt.bundeslaender || [],
        zertifizierungen: profilErstellt.zertifizierungen || [],
        zertifizierungen_freitext: profilErstellt.zertifizierungen_freitext || '',
        kontakt_name: profilErstellt.kontakt_name || '',
        kontakt_email: profilErstellt.kontakt_email || '',
        kontakt_telefon: profilErstellt.kontakt_telefon || '',
        beschreibung: profilErstellt.beschreibung || '',
        einsatzradius_km: profilErstellt.einsatzradius_km || '',
      });
      setBestehendesProfil(profilErstellt);
    }
    setShowForm(true);
    setWizardStep(1);
  };

  const handleDelete = async () => {
    const profilId = profilErstellt?.profil_id || bestehendesProfil?.profil_id;
    if (!profilId) return;
    if (!window.confirm(t('dlWizard.loeschenBestaetigen'))) return;
    try {
      await dienstleisterAPI.profilLoeschen(profilId);
      toast.success(t('dlWizard.profilGeloescht'));
      setProfilErstellt(null);
      setBestehendesProfil(null);
      setShowForm(false);
      setForm({ ...INITIAL_FORM });
    } catch {
      toast.error(t('dlWizard.loeschenFehler'));
    }
  };

  return (
    <div className="min-h-screen bg-warm-50" data-testid="dienstleister-page">
      {/* Header */}
      <header className="border-b border-warm-200 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-primary font-bold" data-testid="logo-link">
            <TreeDeciduous className="h-5 w-5" />
            <span className="font-serif text-lg">ForestHub</span>
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-primary/25 text-primary/70 ml-1">
              {t('dlPage.headerBadge')}
            </Badge>
          </Link>
          {user ? (
            <div className="flex items-center gap-3">
              <span className="text-xs text-muted-foreground hidden sm:inline">{user.name}</span>
              <Badge className="bg-primary/10 text-primary text-[10px] border-0">{t('dlPage.rolleAnbieter')}</Badge>
              <Link to="/projekte" className="text-sm font-medium text-primary hover:text-primary/80 transition-colors" data-testid="header-projekte-link">
                {t('dlPage.headerProjekte')}
              </Link>
            </div>
          ) : (
            <Link to="/anmelden" className="text-sm font-medium text-primary hover:text-primary/80 transition-colors">
              {t('dlPage.anmelden')}
            </Link>
          )}
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 bg-white border-b border-warm-200">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary/10 rounded-full text-primary text-sm font-medium mb-6">
            <Shield className="h-4 w-4" />
            {t('dlPage.heroTag')}
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground leading-tight mb-5" data-testid="dl-hero-titel">
            {t('dlPage.heroTitle')}
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto mb-8" data-testid="dl-hero-sub">
            {t('dlPage.heroDesc')}
          </p>
          <Button
            size="lg"
            onClick={() => startWizard('Basis')}
            className="h-13 px-8 text-base"
            data-testid="dl-hero-cta"
          >
            {t('dlPage.heroCta')}
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-16" data-testid="dl-mehrwert">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="font-serif text-2xl sm:text-3xl font-bold text-foreground text-center mb-3">
            {t('dlPage.mehrwertTitle')}
          </h2>
          <p className="text-muted-foreground text-center max-w-2xl mx-auto mb-10 text-sm sm:text-base">
            {t('dlPage.mehrwertDesc')}
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: Briefcase, title: t('dlPage.mv1Title'), desc: t('dlPage.mv1Desc') },
              { icon: Eye, title: t('dlPage.mv2Title'), desc: t('dlPage.mv2Desc') },
              { icon: MapPin, title: t('dlPage.mv3Title'), desc: t('dlPage.mv3Desc') },
              { icon: Award, title: t('dlPage.mv4Title'), desc: t('dlPage.mv4Desc') },
              { icon: Users, title: t('dlPage.mv5Title'), desc: t('dlPage.mv5Desc') },
              { icon: Shield, title: t('dlPage.mv6Title'), desc: t('dlPage.mv6Desc') },
            ].map((v, i) => (
              <Card key={i} className="border-warm-200">
                <CardContent className="pt-5 pb-5">
                  <v.icon className="h-5 w-5 text-primary mb-3" />
                  <h3 className="text-sm font-semibold text-foreground mb-1.5">{v.title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">{v.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Regionale Übersicht */}
      <section className="py-16 bg-white border-y border-warm-200" data-testid="dl-regional">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="font-serif text-2xl sm:text-3xl font-bold text-foreground text-center mb-3">
            {t('dlPage.regionalTitle')}
          </h2>
          <p className="text-muted-foreground text-center max-w-2xl mx-auto mb-8 text-sm sm:text-base">
            {t('dlPage.regionalDesc')}
          </p>
          <Card className="border-warm-200">
            <CardContent className="pt-6 pb-6">
              <RegionalKarte regionen={regionen} selectedBl={selectedBl} onSelect={(bl) => { trackDlMapClick(bl); setSelectedBl(bl); }} />
            </CardContent>
          </Card>
          <p className="text-xs text-muted-foreground text-center mt-3">
            {t('dlPage.regionalFootnote')}
          </p>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16" data-testid="dl-pricing">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="font-serif text-2xl sm:text-3xl font-bold text-foreground text-center mb-3">
            {t('dlPage.pricingTitle')}
          </h2>
          <p className="text-muted-foreground text-center max-w-2xl mx-auto mb-10 text-sm sm:text-base">
            {t('dlPage.pricingDesc')}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {preisstufen.map((stufe, i) => {
              const isSelected = gewaehlteStufe === stufe.name;
              return (
                <Card key={i} className={`relative transition-all ${isSelected ? 'border-primary shadow-lg ring-2 ring-primary/30' : stufe.hervorgehoben ? 'border-primary/50 shadow-md ring-1 ring-primary/10' : 'border-warm-200'}`} data-testid={`preisstufe-${stufe.name.toLowerCase()}`}>
                  {stufe.hervorgehoben && !isSelected && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="bg-primary text-white text-[10px] px-2">{t('dlPage.pricingEmpfohlen')}</Badge>
                    </div>
                  )}
                  {isSelected && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="bg-primary text-white text-[10px] px-2">{t('dlPage.pricingIhreWahl')}</Badge>
                    </div>
                  )}
                  <CardContent className="pt-6 pb-5 flex flex-col h-full">
                    <h3 className="font-semibold text-foreground text-base mb-0.5">{stufe.name}</h3>
                    <p className="text-lg font-bold text-primary mb-1">{stufe.preis}</p>
                    <p className="text-xs text-muted-foreground mb-4">{stufe.sub}</p>
                    <ul className="space-y-2 flex-1">
                      {stufe.features.map((f, j) => (
                        <li key={j} className="flex items-start gap-2 text-xs text-foreground">
                          <CheckCircle2 className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
                          {f}
                        </li>
                      ))}
                    </ul>
                    <Button
                      variant={isSelected ? 'default' : 'outline'}
                      size="sm"
                      className={`w-full mt-4 ${isSelected ? '' : 'hover:bg-primary/5 hover:border-primary/40'}`}
                      onClick={() => {
                        trackDlPricingView();
                        startWizard(stufe.name);
                      }}
                      data-testid={`stufe-waehlen-${stufe.name.toLowerCase()}`}
                    >
                      {isSelected ? (
                        <><Check className="h-3.5 w-3.5 mr-1.5" /> {t('dlPage.pricingAusgewaehlt')}</>
                      ) : (
                        <><ArrowRight className="h-3.5 w-3.5 mr-1.5" /> {i === 0 ? t('dlPage.pricingKostenlos') : t('dlPage.pricingStufeWaehlen')}</>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <p className="text-xs text-muted-foreground text-center mt-6">
            {t('dlPage.pricingFootnote')}
          </p>
        </div>
      </section>

      {/* Profil-Wizard */}
      <section className="py-16 bg-white border-t border-warm-200" id="profil-section" data-testid="dl-profil-section">
        <div className="max-w-2xl mx-auto px-4">
          {profilErstellt && !showForm ? (
            <div data-testid="profil-erfolg">
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                  <Check className="h-8 w-8 text-emerald-600" />
                </div>
                <h2 className="font-serif text-2xl font-bold text-foreground mb-2">{t('dlWizard.profilErstelltTitle')}</h2>
                <p className="text-muted-foreground mb-1">
                  Ihr Profil wurde angelegt{gewaehlteStufe !== 'Basis' ? ` (Stufe "${gewaehlteStufe}")` : ''}.
                </p>
                <p className="text-xs text-muted-foreground">Profil-ID: {profilErstellt.profil_id}</p>
              </div>

              <Card className="mb-6 border-primary/15">
                <CardContent className="pt-5 pb-5">
                  <h3 className="font-semibold text-foreground mb-3">Ihr Profil</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    {profilErstellt.firma && (
                      <div><span className="text-muted-foreground">Firma:</span> <span className="font-medium">{profilErstellt.firma}</span></div>
                    )}
                    {profilErstellt.kontakt_name && (
                      <div><span className="text-muted-foreground">Kontakt:</span> <span className="font-medium">{profilErstellt.kontakt_name}</span></div>
                    )}
                    {profilErstellt.kontakt_email && (
                      <div><span className="text-muted-foreground">E-Mail:</span> <span className="font-medium">{profilErstellt.kontakt_email}</span></div>
                    )}
                    {profilErstellt.leistungen?.length > 0 && (
                      <div className="sm:col-span-2">
                        <span className="text-muted-foreground">Leistungen:</span>{' '}
                        <span className="font-medium">{profilErstellt.leistungen.join(', ')}</span>
                      </div>
                    )}
                    {profilErstellt.regionen?.length > 0 && (
                      <div className="sm:col-span-2">
                        <span className="text-muted-foreground">Regionen:</span>{' '}
                        <span className="font-medium">{profilErstellt.regionen.join(', ')}</span>
                      </div>
                    )}
                    <div>
                      <span className="text-muted-foreground">Stufe:</span>{' '}
                      <span className="font-medium">{gewaehlteStufe} ({preisstufen.find(s => s.name === gewaehlteStufe)?.preis})</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="grid grid-cols-2 gap-3 max-w-md mx-auto" data-testid="erfolg-aktionen">
                <Button variant="outline" onClick={startEdit} data-testid="btn-profil-aendern">
                  {t('dlWizard.profilAendern')}
                </Button>
                <Button variant="outline" onClick={() => navigate('/registrieren?role=anbieter')} data-testid="btn-dashboard">
                  {t('dlWizard.projekteAnbieter')}
                </Button>
                <Button variant="outline" onClick={() => navigate('/')} data-testid="btn-startseite">
                  {t('dlWizard.zurStartseite')}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleDelete}
                  className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                  data-testid="btn-profil-loeschen"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                  {t('dlWizard.profilLoeschen')}
                </Button>
              </div>
            </div>
          ) : showForm ? (
            <div data-testid="dienstleister-wizard">
              {/* Stepper */}
              <nav className="mb-8" aria-label="Fortschritt" data-testid="dl-wizard-stepper">
                <ol className="flex items-center gap-0">
                  {WIZARD_STEP_KEYS.map((stepKey, i) => {
                    const nr = i + 1;
                    const done = wizardStep > nr;
                    const active = wizardStep === nr;
                    return (
                      <li key={nr} className="flex items-center flex-1 last:flex-none">
                        <button
                          disabled={nr > wizardStep}
                          onClick={() => nr <= wizardStep && setWizardStep(nr)}
                          className={`flex items-center gap-1.5 text-xs sm:text-sm font-medium transition-colors ${
                            active ? 'text-primary' : done ? 'text-primary/60 hover:text-primary' : 'text-gray-400'
                          }`}
                          data-testid={`dl-step-${nr}-btn`}
                        >
                          <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all shrink-0 ${
                            active ? 'bg-primary text-white border-primary' :
                            done ? 'bg-primary/10 text-primary border-primary/30' :
                            'bg-gray-100 text-gray-400 border-gray-200'
                          }`}>
                            {done ? '\u2713' : nr}
                          </span>
                          <span className="hidden md:inline">{t(`dlWizard.${stepKey}`)}</span>
                        </button>
                        {i < WIZARD_STEP_KEYS.length - 1 && (
                          <div className={`flex-1 h-0.5 mx-2 rounded ${done ? 'bg-primary/30' : 'bg-gray-200'}`} />
                        )}
                      </li>
                    );
                  })}
                </ol>
                <p className="text-xs text-muted-foreground mt-2 text-center" data-testid="dl-step-label">
                  {t('dlWizard.schrittVon')} {wizardStep} {t('dlWizard.von')} {WIZARD_STEP_KEYS.length}: {t(`dlWizard.${WIZARD_STEP_KEYS[wizardStep - 1]}`)}
                </p>
              </nav>

              {/* Resume Draft Dialog */}
              {hasDraft && draftLoaded && wizardStep === 1 && !bestehendesProfil && (
                <Card className="mb-6 border-primary/20 bg-primary/[0.03]" data-testid="resume-draft-dialog">
                  <CardContent className="p-5 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <Save className="h-5 w-5 text-primary shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{t('dlWizard.draftTitle')}</p>
                        <p className="text-xs text-gray-500">{t('dlWizard.draftDesc')}</p>
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button size="sm" onClick={resumeDraft} data-testid="resume-draft-btn">
                        <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                        {t('dlWizard.draftFortfahren')}
                      </Button>
                      <Button size="sm" variant="outline" onClick={dismissDraft} data-testid="dismiss-draft-btn">
                        {t('dlWizard.draftVerwerfen')}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Gewählte Stufe Info */}
              {gewaehlteStufe !== 'Basis' && (
                <div className="mb-6 p-3 rounded-lg border border-primary/20 bg-primary/[0.03] flex items-center justify-between" data-testid="gewaehlte-stufe-info">
                  <div className="flex items-center gap-2">
                    <Star className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium text-foreground">Stufe: {gewaehlteStufe}</span>
                    <span className="text-sm text-primary font-semibold">
                      ({preisstufen.find(s => s.name === gewaehlteStufe)?.preis})
                    </span>
                  </div>
                  <button onClick={() => setGewaehlteStufe('Basis')} className="text-xs text-muted-foreground hover:text-foreground">
                    {t('dlWizard.stufeAufBasis')}
                  </button>
                </div>
              )}

              {/* Step Content */}
              <Card className="border-warm-200">
                <CardContent className="p-6 sm:p-8">
                  {wizardStep === 1 && <StepBasisdaten form={form} setForm={setForm} />}
                  {wizardStep === 2 && <StepLeistungen form={form} setForm={setForm} optionen={optionen} />}
                  {wizardStep === 3 && <StepRegionen form={form} setForm={setForm} optionen={optionen} />}
                  {wizardStep === 4 && <StepZertifikate form={form} setForm={setForm} optionen={optionen} />}
                  {wizardStep === 5 && (
                    <StepZusammenfassung
                      form={form}
                      gewaehlteStufe={gewaehlteStufe}
                      preisstufen={preisstufen}
                      sending={sending}
                      onAbsenden={absenden}
                      istBearbeitung={!!bestehendesProfil?.profil_id}
                    />
                  )}
                </CardContent>
              </Card>

              {/* Navigation */}
              {wizardStep < 5 && (
                <div className="flex items-center justify-between mt-6" data-testid="wizard-navigation">
                  <Button
                    variant="ghost"
                    onClick={wizardStep === 1 ? () => setShowForm(false) : prevStep}
                    data-testid="wizard-back-btn"
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    {wizardStep === 1 ? t('dlWizard.abbrechen') : t('dlWizard.zurueck')}
                  </Button>
                  <Button
                    onClick={nextStep}
                    disabled={!canAdvance(wizardStep)}
                    data-testid="wizard-next-btn"
                  >
                    {t('dlWizard.weiter')}
                    <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              )}
              {wizardStep === 5 && (
                <div className="flex items-center justify-start mt-6">
                  <Button variant="ghost" onClick={prevStep} data-testid="wizard-back-btn-final">
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    {t('dlWizard.zurueck')}
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center">
              <h2 className="font-serif text-2xl sm:text-3xl font-bold text-foreground mb-2">
                {t('dlWizard.bereitTitle')}
              </h2>
              <p className="text-muted-foreground max-w-lg mx-auto mb-8 text-sm sm:text-base">
                {t('dlWizard.bereitDesc')}
              </p>
              <Button size="lg" onClick={() => startWizard('Basis')} className="h-13 px-8 text-base" data-testid="profil-starten-btn">
                <Shield className="h-5 w-5 mr-2" />
                {t('dlWizard.profilStartenBtn')}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-warm-200 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <TreeDeciduous className="h-3.5 w-3.5 text-primary/50" />
            <span>{t('dlPage.footerDienstleister')}</span>
          </div>
          <div className="flex gap-4">
            <Link to="/datenschutz" className="hover:text-foreground transition-colors">{t('dlPage.footerDatenschutz')}</Link>
            <Link to="/impressum" className="hover:text-foreground transition-colors">{t('dlPage.footerImpressum')}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
